/**
 * Manual calendar — single slot picker using FullCalendar directly.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import type { CalendarHandle } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { CalendarToolbar } from './CalendarToolbar';
import {
  AvailableSlotContent,
  BookedEventContent,
  BusyEventContent,
} from './events';
import {
  useCalendarEventHandlers,
  useManualCalendarEvents,
  type ManualCalendarExternalEvent,
} from './hooks';
import {
  busyEventChunkBounds,
  getScrollTimeForNow,
  getScrollTime,
  MAX_DURATION_MINUTES,
  buildBookedBufferZoneEvents,
} from './events';
import type { ScheduleSlot } from '../state/useScheduleSharedState';
import './calendar.css';

interface ManualCalendarProps {
  duration: number;
  timezone?: string;
  interviewName?: string;
  candidateName?: string;
  bookedSlot?: ScheduleSlot | null;
  externalEvents?: ManualCalendarExternalEvent[];
  /** Extra FullCalendar events the parent wants to render on top (available slots, notice overlay). */
  overlayEvents?: EventInput[];
  onSlotSelect: (_slot: { start: string; end: string }) => void;
  onTimezoneChange?: (_tz: string) => void;
  /** Fired when the visible week changes — for calendar busy fetch window */
  onActiveRangeChange?: (_range: { start: string; end: string }) => void;
  isEdit?: boolean;
  goToDate?: Date | string;
  /** Buffer clearance (mins) drawn as the diagonal zone around the booked slot. */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * SCH-57 — the calendar is rendering candidate-collected availability
   * (green windows) instead of computed interviewer slots. Adds the
   * `has-candidate-availability` root class that activates the CSS
   * column-split (busy left / candidate window right) and switches the
   * `candidate-availability` event renderer to the green selectable block.
   */
  hasCandidateAvailability?: boolean;
}

export const ManualCalendar = forwardRef<CalendarHandle, ManualCalendarProps>(
  (
    {
      duration,
      timezone: timezoneProp,
      interviewName,
      candidateName,
      bookedSlot,
      externalEvents = [],
      overlayEvents = [],
      onSlotSelect,
      onTimezoneChange,
      onActiveRangeChange,
      isEdit = false,
      goToDate,
      bufferBeforeMinutes = 0,
      bufferAfterMinutes = 0,
      hasCandidateAvailability = false,
    },
    ref
  ) => {
    const calendarRef = useRef<FullCalendar>(null);
    const [timezone, setTimezone] = useState(
      () => timezoneProp || moment.tz.guess()
    );

    // Ref-based callback for toolbar to subscribe to datesSet without re-rendering
    const onDatesSetRef = useRef<(() => void) | null>(null);

    const handleTimezoneChange = useCallback(
      (tz: string) => {
        setTimezone(tz);
        onTimezoneChange?.(tz);
      },
      [onTimezoneChange]
    );

    useImperativeHandle(ref, () => ({
      goToDate: (date) =>
        calendarRef.current
          ?.getApi()
          ?.gotoDate(typeof date === 'string' ? new Date(date) : date),
      next: () => calendarRef.current?.getApi()?.next(),
      prev: () => calendarRef.current?.getApi()?.prev(),
      today: () => calendarRef.current?.getApi()?.today(),
      getTimezone: () => timezone,
      setTimezone: (tz) => handleTimezoneChange(tz),
      clearAll: () => {},
      getSlots: () =>
        bookedSlot
          ? [
              {
                id: 'booked',
                start: bookedSlot.start,
                end: bookedSlot.end,
                date: bookedSlot.start.split('T')[0],
              },
            ]
          : [],
    }));

    // In edit mode the booked slot is seeded from payloadRef via an async
    // useEffect in DetailsStep, so `bookedSlot` is null on the very first
    // render and FullCalendar commits to `initialDate=undefined` (= today).
    // Navigate imperatively once the slot arrives so the recruiter always
    // lands on the booked event's week, scrolled to its time position.
    const didNavigateRef = useRef(false);
    useEffect(() => {
      if (!isEdit || didNavigateRef.current || !bookedSlot?.start) return;
      const api = calendarRef.current?.getApi();
      if (!api) return;
      didNavigateRef.current = true;
      api.gotoDate(new Date(bookedSlot.start));
      api.scrollToTime(
        getScrollTime(moment.tz(bookedSlot.start, timezone).hour())
      );
    }, [isEdit, bookedSlot, timezone]);

    const { events: composedEvents } = useManualCalendarEvents({
      bookedSlot: bookedSlot ?? null,
      externalEvents,
      timezone,
      interviewName,
    });

    // The current interview's buffer clearance — drawn as the diagonal
    // `bg-slot-buffer` zone around the booked slot. The matching BE
    // buffer calendar events are filtered out of busy upstream
    // (`useBusyCalendarEvents` → `isOwnInterviewBufferEvent`).
    const bufferZoneEvents = useMemo<EventInput[]>(() => {
      if (!bookedSlot?.start || !bookedSlot?.end) return [];
      if (bufferBeforeMinutes <= 0 && bufferAfterMinutes <= 0) return [];
      return buildBookedBufferZoneEvents({
        bookedStartIso: bookedSlot.start,
        bookedEndIso: bookedSlot.end,
        bufferBeforeMinutes,
        bufferAfterMinutes,
        timezone,
      });
    }, [bookedSlot, bufferBeforeMinutes, bufferAfterMinutes, timezone]);

    const events = [...composedEvents, ...overlayEvents, ...bufferZoneEvents];

    const { handleSelect, handleEventDrop, handleEventResize } =
      useCalendarEventHandlers({
        calendarRef,
        timezone,
        duration,
        onSlotSelect,
        bufferBeforeMinutes,
      });

    // Earliest the booked slot may start — `now` plus the before-buffer.
    // The before-buffer is part of the interview's required clearance, so
    // it must also sit at/after `now`: a slot can't start so early that
    // its before-buffer falls in the past. Parent zeroes the minutes when
    // buffer is off, so a positive value already means "enabled".
    const earliestStartMs = useCallback(
      () =>
        moment().tz(timezone).valueOf() +
        (bufferBeforeMinutes > 0 ? bufferBeforeMinutes * 60_000 : 0),
      [timezone, bufferBeforeMinutes]
    );

    const initialDate =
      isEdit && bookedSlot?.start
        ? new Date(bookedSlot.start)
        : goToDate
          ? typeof goToDate === 'string'
            ? new Date(goToDate)
            : goToDate
          : undefined;

    const renderEventContent = useCallback(
      (eventInfo: {
        event: {
          id: string;
          title: string;
          startStr: string;
          endStr: string;
          extendedProps: Record<string, unknown>;
        };
      }) => {
        const category = eventInfo.event.extendedProps?.category as string;
        // Buffer clearance zones are a wash only — no inner label/box.
        // The diagonal stripe comes from the `.bg-slot-buffer` CSS on the
        // event root; render empty so FC doesn't draw a title box.
        if (category === 'background') {
          return <></>;
        }
        if (category === 'booked') {
          return (
            <BookedEventContent
              interviewName={interviewName}
              start={eventInfo.event.startStr}
              end={eventInfo.event.endStr}
              timezone={timezone}
            />
          );
        }
        if (category === 'busy') {
          const tooltipTitle =
            (eventInfo.event.extendedProps?.tooltipTitle as
              | string
              | undefined) ?? undefined;
          const startMs = new Date(eventInfo.event.startStr).getTime();
          const endMs = new Date(eventInfo.event.endStr).getTime();
          // Past events can't seed a new slot (the click handler
          // already drops past clicks via `snapped.isBefore(...)`),
          // but the cursor was still showing pointer — misleading.
          // Pass `onSlotClick={undefined}` so BusyEventContent flips
          // to `cursor: default` and skips the click wiring entirely.
          const isPastEvent = endMs <= Date.now();
          return (
            <BusyEventContent
              title={eventInfo.event.title}
              tooltipTitle={tooltipTitle}
              isFree={eventInfo.event.extendedProps?.status === 'free'}
              start={eventInfo.event.startStr}
              end={eventInfo.event.endStr}
              timezone={timezone}
              onSlotClick={
                isPastEvent
                  ? undefined
                  : (e) => {
                      const { chunkStartMs, chunkEndMs } = busyEventChunkBounds(
                        e.currentTarget,
                        startMs,
                        endMs,
                        timezone
                      );
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relY = Math.max(
                        0,
                        Math.min(rect.height, e.clientY - rect.top)
                      );
                      const proportion =
                        rect.height > 0 ? relY / rect.height : 0;
                      const clickMs =
                        chunkStartMs + proportion * (chunkEndMs - chunkStartMs);
                      const gridMs = 15 * 60_000;
                      const snapped = moment.tz(
                        Math.round(clickMs / gridMs) * gridMs,
                        timezone
                      );
                      if (snapped.isBefore(moment().tz(timezone))) return;
                      onSlotSelect({
                        start: snapped.toISOString(),
                        end: snapped
                          .clone()
                          .add(duration, 'minutes')
                          .toISOString(),
                      });
                      calendarRef.current?.getApi()?.unselect();
                    }
              }
            />
          );
        }
        if (category === 'available') {
          return (
            <AvailableSlotContent
              startIso={eventInfo.event.startStr}
              endIso={eventInfo.event.endStr}
              duration={duration}
              timezone={timezone}
              onSelect={onSlotSelect}
            />
          );
        }
        if (category === 'candidate-availability') {
          // Green candidate-collected window — same hover-to-select
          // affordance as the blue block, candidate name on the third
          // line (legacy parity). Mutual/no-mutual opacity + the column
          // split come from CSS via the event's class names.
          return (
            <AvailableSlotContent
              startIso={eventInfo.event.startStr}
              endIso={eventInfo.event.endStr}
              duration={duration}
              timezone={timezone}
              onSelect={onSlotSelect}
              tone="candidate"
              subLabel={
                (eventInfo.event.extendedProps?.candidateName as
                  | string
                  | undefined) || candidateName
              }
            />
          );
        }
        return (
          <Box p="2px 4px" fontSize="11px" overflow="hidden">
            {eventInfo.event.title}
          </Box>
        );
      },
      [interviewName, candidateName, timezone, duration, onSlotSelect]
    );

    return (
      <Flex
        flexDir="column"
        h="100%"
        w="100%"
        className={`im-calendar${
          hasCandidateAvailability ? ' has-candidate-availability' : ''
        }`}
      >
        <CalendarToolbar
          calendarRef={calendarRef}
          timezone={timezone}
          onTimezoneChange={handleTimezoneChange}
          disablePastNavigation
          onDatesSetRef={onDatesSetRef}
        />
        <Box
          flex="1"
          minH={0}
          position="relative"
          overflow="visible"
          className="im-calendar-fc-scroll-host"
        >
          <FullCalendar
            key={timezone}
            ref={calendarRef}
            plugins={[interactionPlugin, timeGridPlugin, momentTimezonePlugin]}
            nowIndicator={true}
            initialView="timeGridWeek"
            initialDate={initialDate}
            dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
            dayHeaderContent={(args) => {
              // Decode in the calendar's tz, not the browser's. FC's
              // fake-UTC Date matches the calendar wall-clock; reading
              // through browser-local moment() shifts the day name +
              // number AND mis-locates the "today" highlight when tz
              // differs (e.g. PST browser + IST calendar).
              const date = moment.tz(args.date, timezone);
              const isToday = date.isSame(moment().tz(timezone), 'day');
              return (
                <Flex
                  direction="column"
                  align="center"
                  py="6px"
                  color={isToday ? 'primary-500' : 'secondary-700'}
                >
                  <KText
                    fontSize="11px"
                    fontWeight="500"
                    textTransform="uppercase"
                  >
                    {date.format('ddd')}
                  </KText>
                  <KText fontSize="18px" fontWeight="600">
                    {date.format('D')}
                  </KText>
                </Flex>
              );
            }}
            headerToolbar={false}
            selectable={true}
            selectMirror={false}
            dayMaxEvents
            weekends
            height="100%"
            allDaySlot={false}
            slotDuration="00:15:00"
            // FC snap is the FINE grid (5 min) so drag-resize can land
            // anywhere from 5 → 1440 min. `useCalendarEventHandlers`
            // re-snaps to the manual grid on release (5-min steps below
            // 15 min, 15-min steps above) — see `snapManualDurationMinutes`.
            snapDuration="00:05:00"
            // FC defaults `eventResizeMinDuration` to 30 min, which
            // silently blocks dragging below it. Drop to the absolute
            // floor (5 min) so the post-release snap can reach it.
            eventResizeMinDuration="00:05:00"
            slotLabelInterval={{ hours: 1 }}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            expandRows={true}
            timeZone={timezone}
            firstDay={0}
            stickyHeaderDates={true}
            slotEventOverlap={true}
            scrollTime={getScrollTimeForNow(timezone)}
            eventOrderStrict={true}
            // Ascending priority: FullCalendar renders the seg that sorts
            // LAST on top (deeper stackDepth → higher z-index). So higher
            // `EVENT_PRIORITY` = later in order = on top — booked (20) sits
            // above busy (10). A leading `-` (descending) inverts this and
            // buries the booked slot behind busy events.
            eventOrder="extendedProps.priority,start_time"
            events={events}
            datesSet={(arg) => {
              onDatesSetRef.current?.();
              if (onActiveRangeChange && arg?.start && arg?.end) {
                const tz = timezone;
                onActiveRangeChange({
                  start: moment.tz(arg.start, tz).toISOString(),
                  end: moment.tz(arg.end, tz).toISOString(),
                });
              }
            }}
            select={handleSelect}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventContent={renderEventContent}
            eventDidMount={(info) => {
              // FullCalendar's BgEvent renderer only forwards
              // `backgroundColor` to the DOM (see `@fullcalendar/core`
              // internal-common.js BgEvent), so `borderColor` /
              // `textColor` from the event input are dropped for
              // `display: 'background'` events. Inject them inline here
              // so the interviewer-pastel dashed border + label colour
              // survive on free events. `BusyEventContent` renders the
              // title + time with `color: inherit`, so setting `color`
              // on the root cascades down without needing a selector.
              if (info.event.extendedProps?.status !== 'free') return;
              const { borderColor, textColor } = info.event;
              if (borderColor) {
                info.el.style.borderStyle = 'dashed';
                info.el.style.borderWidth = '1px';
                info.el.style.borderColor = borderColor;
              }
              if (textColor) {
                info.el.style.color = textColor;
              }
            }}
            selectAllow={(selectInfo) => {
              const dur = moment
                .tz(selectInfo.endStr, timezone)
                .diff(moment.tz(selectInfo.startStr, timezone), 'minutes');
              return (
                selectInfo.start.getTime() >= earliestStartMs() &&
                dur <= MAX_DURATION_MINUTES
              );
            }}
            eventAllow={(dropInfo) =>
              dropInfo.start.getTime() >= earliestStartMs()
            }
            slotLabelContent={({ text }) => (
              <KText variant="small_text" w="47px" alignItems="center">
                {text}
              </KText>
            )}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              omitZeroMinute: true,
              meridiem: true,
              separator: ' - ',
            }}
          />
        </Box>
      </Flex>
    );
  }
);

ManualCalendar.displayName = 'ManualCalendar';
